<template>
    <div class="rtl overflow-y-scroll">
        <app-list
            ref="list"
            v-model="items"
            class="ltr"
            :searchable="!isInvestor()"
            :searching="searching"
            @search="onSearch"
        />
    </div>
</template>

<script>
import AccountTypeEnum from '@constants/enums/account/type';

import { AppListItem, Investor, Account } from '@/models';

import Sidebar from '@/components/sidebars/Sidebar';

export default {
    name: 'TransactionsSidebar',
    extends: Sidebar,
    data: () => ({
        items: [],
        searching: false
    }),
    computed: {
        investorId() {
            let investorId = this.$route.query.investor_id;

            if (!investorId) {
                investorId = this.getQueryParam('investor_id');
            }

            if (!investorId) {
                investorId = this.$store.getters['app/query_selector']('investor_id');
            }

            return investorId;
        }
    },
    async mounted() {
        await this.trySetup();

        await this.$nextTick();

        const activeEl = document.querySelector('.app-list__item--active');

        if (activeEl) {
            activeEl.scrollIntoView({
                behavior: 'smooth'
            });
        }
    },
    methods: {
        async setup() {
            if (this.isInvestor()) {
                await this.setupInvestor();
            } else {
                await this.setupOther();
            }

            this.items = await Promise.all(this.items.map(item => item.setup(true)));
        },
        toAppListItem(investor) {
            return new AppListItem({
                id: investor.id,
                name: investor.name,
                route: {
                    name: 'Transactions',
                    query: { investor_id: investor.id }
                },
                populate: () => async () => {
                    let items = (
                        await Account.api.index(null, null, null, {
                            investor_id: `${this.getFilterOperator('is')}:${investor.id}`
                        })
                    ).data.map(account => {
                        account = new Account(account);
                        return new AppListItem({
                            id: account.id,
                            name: account.name,
                            icon: account.icon,
                            tooltip: account.type_name,
                            route: {
                                name: 'Transactions',
                                query: { investor_id: investor.id, account_id: account.id }
                            }
                        });
                    });

                    if (this.$route.query.account_id && this.$route.query.investor_id === investor.id) {
                        try {
                            const found = (await Account.api.get(this.$route.query.account_id)).data;
                            const itemIndex = items.find(item => item.id === found.id);

                            if (found && itemIndex !== 0 && !itemIndex) {
                                items.unshift(
                                    new AppListItem({
                                        id: found.id,
                                        name: found.name,
                                        route: {
                                            name: 'Transactions',
                                            query: { investor_id: investor.id, account_id: found.id }
                                        }
                                    })
                                );
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    }

                    return items;
                }
            });
        },
        async setupInvestor() {
            this.items = [
                new AppListItem({
                    id: 'investor',
                    name: this.$t('all_transactions'),
                    route: {
                        name: 'Transactions',
                        query: { investor_id: this.getOrganisationId() }
                    },
                    populate: () => async () => {
                        let items = (
                            await Account.api.index(null, null, null, {
                                investor_id: `${this.getFilterOperator('is')}:${this.getOrganisationId()}`,
                                type: `${this.getFilterOperator('is_not')}:${AccountTypeEnum.FEE}`
                            })
                        ).data.map(account => {
                            account = new Account(account);
                            return new AppListItem({
                                id: account.id,
                                name: account.name,
                                icon: account.icon,
                                tooltip: account.type_name,
                                route: {
                                    name: 'Transactions',
                                    query: { investor_id: this.getOrganisationId(), account_id: account.id }
                                }
                            });
                        });

                        return items;
                    }
                })
            ];

            const parent = this.items.find(item => item.id === 'investor');

            parent.items = await parent.populate();
            parent.$expanded = true;
        },
        async setupOther() {
            let items = (await Investor.api.index(null, 100)).data.map(investor => {
                return this.toAppListItem(investor);
            });

            if (this.investorId) {
                try {
                    const existing = items.find(item => item.id === this.investorId);
                    if (!existing) {
                        const found = (await Investor.api.get(this.investorId)).data;
                        if (found) {
                            items.unshift(this.toAppListItem(found));
                        }
                    }
                } catch (error) {
                    console.error(error);
                }
            }

            this.items = items;
        },
        async onSearch(searchQuery) {
            this.searching = true;

            let items = [];

            if (searchQuery) {
                items = (await Investor.api.search(searchQuery)).data.map(investor => {
                    return this.toAppListItem(investor);
                });
            } else {
                items = (await Investor.api.index(null, 100)).data.map(investor => {
                    return this.toAppListItem(investor);
                });
            }

            try {
                this.items = await Promise.all(items.map(item => item.setup(true)));
            } catch (error) {
                console.error(error);
            }

            this.searching = false;
        }
    }
};
</script>
